import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Fingerprint,
  Eye,
  Sparkles,
  AlertTriangle,
  History,
  Check,
  X,
  MessageSquare,
  Edit,
  Clock,
  FileText,
  ChevronRight,
  ChevronDown,
  Shield,
  HelpCircle,
  RefreshCw,
  TrendingUp,
  Calendar,
  GitBranch,
  Info,
  Layers
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

/**
 * IdentityFormation Page
 * 
 * The dedicated workspace for identity formation.
 * 
 * Four conceptual areas:
 * 1. What HumaTwin knows - Confirmed stable identity elements
 * 2. What HumaTwin is noticing - Emerging patterns and candidate traits
 * 3. What remains uncertain - Weak, contextual, or contradictory evidence
 * 4. What changed over time - Identity evolution and superseded traits
 * 
 * UI Language Rules (Non-negotiable):
 * - "HumaTwin noticed..." NOT "You are..."
 * - "This pattern appeared several times..." NOT "Your personality is..."
 * - "Would you consider this part of your identity?" NOT "HumaTwin has determined..."
 * - The interface is a MIRROR, not an EVALUATOR
 */

export default function IdentityFormation() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [confirmedTraits, setConfirmedTraits] = useState([]);
  const [candidateTraits, setCandidateTraits] = useState([]);
  const [contradictions, setContradictions] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI states
  const [expandedSection, setExpandedSection] = useState('noticed'); // noticed, confirmed, uncertain, history
  const [selectedTrait, setSelectedTrait] = useState(null);
  const [showTraitDetail, setShowTraitDetail] = useState(false);
  const [traitDetail, setTraitDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // HT-Core-02: Analysis states
  const [traitAnalysis, setTraitAnalysis] = useState(null);
  const [traitTimeline, setTraitTimeline] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  
  // Action states
  const [showReframeDialog, setShowReframeDialog] = useState(false);
  const [reframeText, setReframeText] = useState('');
  const [showContextualizeDialog, setShowContextualizeDialog] = useState(false);
  const [contextualNote, setContextualNote] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectPermanent, setRejectPermanent] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, confirmedRes, candidateRes, contradictionsRes, evolutionRes] = await Promise.all([
        apiClient.get('/identity-evidence/summary'),
        apiClient.get('/identity-evidence/candidates?state=confirmed'),
        apiClient.get('/identity-evidence/candidates?state=observed,emerging,candidate,contested'),
        apiClient.get('/identity-evidence/contradictions'),
        apiClient.get('/identity-evidence/history?limit=20')
      ]);
      
      setSummary(summaryRes.data);
      setConfirmedTraits(confirmedRes.data || []);
      setCandidateTraits(candidateRes.data || []);
      setContradictions(contradictionsRes.data || []);
      setEvolution(evolutionRes.data || []);
    } catch (error) {
      console.error('Failed to fetch identity formation data:', error);
      toast.error(t('identityFormation.loadError', 'Failed to load identity formation data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // HT-Core-02: Set document direction for proper RTL
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  // Fetch trait detail with provenance
  const fetchTraitDetail = async (traitId) => {
    setLoadingDetail(true);
    setTraitAnalysis(null);
    setTraitTimeline(null);
    try {
      const res = await apiClient.get(`/identity-evidence/candidates/${traitId}`);
      setTraitDetail(res.data);
      setShowTraitDetail(true);
      
      // HT-Core-02: Fetch analysis data in parallel
      fetchTraitAnalysis(traitId);
    } catch (error) {
      console.error('Failed to fetch trait detail:', error);
      toast.error(t('identityFormation.detailError', 'Failed to load trait details'));
    } finally {
      setLoadingDetail(false);
    }
  };

  // HT-Core-02: Fetch analysis for a trait
  const fetchTraitAnalysis = async (traitId) => {
    setLoadingAnalysis(true);
    try {
      const [analysisRes, timelineRes] = await Promise.all([
        apiClient.get(`/identity-evidence/candidates/${traitId}/analysis`),
        apiClient.get(`/identity-evidence/candidates/${traitId}/timeline`)
      ]);
      setTraitAnalysis(analysisRes.data);
      setTraitTimeline(timelineRes.data);
    } catch (error) {
      console.error('Failed to fetch trait analysis:', error);
      // Non-critical, don't show toast
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Action handlers
  const handleConfirm = async (traitId) => {
    setActionLoading(true);
    try {
      await apiClient.post(`/identity-evidence/candidates/${traitId}/confirm`, {});
      toast.success(t('identityFormation.confirmed', 'Pattern confirmed as part of your identity'));
      setShowTraitDetail(false);
      fetchData();
    } catch (error) {
      console.error('Confirm failed:', error);
      toast.error(error.response?.data?.detail || t('identityFormation.actionError', 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTrait) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/identity-evidence/candidates/${selectedTrait.trait_id}/reject`, {
        reason: rejectReason || null,
        permanent: rejectPermanent
      });
      toast.success(t('identityFormation.rejected', 'Pattern rejected. HumaTwin will not ask about this again.'));
      setShowRejectDialog(false);
      setShowTraitDetail(false);
      setRejectReason('');
      setRejectPermanent(false);
      fetchData();
    } catch (error) {
      console.error('Reject failed:', error);
      toast.error(error.response?.data?.detail || t('identityFormation.actionError', 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReframe = async () => {
    if (!selectedTrait || !reframeText.trim()) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/identity-evidence/candidates/${selectedTrait.trait_id}/reframe`, {
        new_text: reframeText.trim()
      });
      toast.success(t('identityFormation.reframed', 'Pattern reframed in your words'));
      setShowReframeDialog(false);
      setReframeText('');
      fetchData();
    } catch (error) {
      console.error('Reframe failed:', error);
      toast.error(error.response?.data?.detail || t('identityFormation.actionError', 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleContextualize = async () => {
    if (!selectedTrait || !contextualNote.trim()) return;
    setActionLoading(true);
    try {
      await apiClient.post(`/identity-evidence/candidates/${selectedTrait.trait_id}/contextualize`, {
        contextual_note: contextualNote.trim()
      });
      toast.success(t('identityFormation.contextualized', 'Pattern marked as contextual'));
      setShowContextualizeDialog(false);
      setContextualNote('');
      fetchData();
    } catch (error) {
      console.error('Contextualize failed:', error);
      toast.error(error.response?.data?.detail || t('identityFormation.actionError', 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Helpers
  const getStateColor = (state) => {
    const colors = {
      'observed': 'bg-gray-100 text-gray-700',
      'emerging': 'bg-blue-100 text-blue-700',
      'candidate': 'bg-amber-100 text-amber-700',
      'confirmed': 'bg-green-100 text-green-700',
      'contested': 'bg-red-100 text-red-700',
      'rejected': 'bg-gray-200 text-gray-500',
      'superseded': 'bg-purple-100 text-purple-700'
    };
    return colors[state] || colors['observed'];
  };

  const getStateLabel = (state) => {
    const labels = {
      'observed': t('identityFormation.states.observed', 'Observed'),
      'emerging': t('identityFormation.states.emerging', 'Emerging'),
      'candidate': t('identityFormation.states.candidate', 'Candidate'),
      'confirmed': t('identityFormation.states.confirmed', 'Confirmed'),
      'contested': t('identityFormation.states.contested', 'Uncertain'),
      'rejected': t('identityFormation.states.rejected', 'Rejected'),
      'superseded': t('identityFormation.states.superseded', 'Superseded')
    };
    return labels[state] || state;
  };

  // HT-Core-02: Evidence strength helper
  const getStrengthLabel = (level) => {
    const labels = {
      'weak': t('identityFormation.strength.weak', 'Initial observation'),
      'developing': t('identityFormation.strength.developing', 'Early pattern forming'),
      'substantial': t('identityFormation.strength.substantial', 'Developing pattern'),
      'strong': t('identityFormation.strength.strong', 'Strong recurring pattern'),
      'user_confirmed': t('identityFormation.strength.userConfirmed', 'Confirmed by you')
    };
    return labels[level] || level;
  };

  const getStrengthColor = (level) => {
    const colors = {
      'weak': 'bg-gray-100 text-gray-600',
      'developing': 'bg-blue-100 text-blue-600',
      'substantial': 'bg-amber-100 text-amber-700',
      'strong': 'bg-green-100 text-green-700',
      'user_confirmed': 'bg-emerald-100 text-emerald-700'
    };
    return colors[level] || colors['weak'];
  };

  // HT-Core-02: Evolution state helper
  const getEvolutionLabel = (state) => {
    const labels = {
      'stable': t('identityFormation.evolution.stable', 'Stable'),
      'shifting': t('identityFormation.evolution.shifting', 'Possibly changing'),
      'contested': t('identityFormation.evolution.contested', 'Conflicting evidence'),
      'dormant': t('identityFormation.evolution.dormant', 'Dormant'),
      'resurgent': t('identityFormation.evolution.resurgent', 'Resurgent'),
      'superseded': t('identityFormation.evolution.superseded', 'Superseded')
    };
    return labels[state] || state;
  };

  const getEvolutionColor = (state) => {
    const colors = {
      'stable': 'bg-green-100 text-green-700',
      'shifting': 'bg-amber-100 text-amber-700',
      'contested': 'bg-red-100 text-red-700',
      'dormant': 'bg-gray-100 text-gray-600',
      'resurgent': 'bg-blue-100 text-blue-700',
      'superseded': 'bg-purple-100 text-purple-700'
    };
    return colors[state] || colors['stable'];
  };

  // HT-Core-02: Context helper
  const getContextLabel = (context) => {
    const labels = {
      'global': t('identityFormation.context.global', 'Across all areas'),
      'professional': t('identityFormation.context.professional', 'Professional'),
      'family': t('identityFormation.context.family', 'Family'),
      'relationships': t('identityFormation.context.relationships', 'Relationships'),
      'financial': t('identityFormation.context.financial', 'Financial'),
      'health': t('identityFormation.context.health', 'Health'),
      'creative': t('identityFormation.context.creative', 'Creative'),
      'social': t('identityFormation.context.social', 'Social'),
      'political': t('identityFormation.context.political', 'Political'),
      'crisis': t('identityFormation.context.crisis', 'Crisis situations'),
      'private': t('identityFormation.context.private', 'Private reflection'),
      'unknown': t('identityFormation.context.unknown', 'Unknown')
    };
    return labels[context] || context;
  };

  const getDimensionLabel = (dimension) => {
    const labels = {
      'values': t('identityFormation.dimensions.values', 'Values'),
      'priorities': t('identityFormation.dimensions.priorities', 'Priorities'),
      'boundaries': t('identityFormation.dimensions.boundaries', 'Boundaries'),
      'red_lines': t('identityFormation.dimensions.redLines', 'Red Lines'),
      'decision_style': t('identityFormation.dimensions.decisionStyle', 'Decision Style'),
      'communication_style': t('identityFormation.dimensions.communicationStyle', 'Communication Style'),
      'long_term_goals': t('identityFormation.dimensions.longTermGoals', 'Long-term Goals'),
      'recurring_preferences': t('identityFormation.dimensions.preferences', 'Preferences'),
      'worldview': t('identityFormation.dimensions.worldview', 'Worldview')
    };
    return labels[dimension] || dimension;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Section toggle
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Render trait card
  const renderTraitCard = (trait, showActions = true) => (
    <div 
      key={trait.trait_id}
      className={`p-4 bg-white rounded-xl border border-gray-200 hover:border-primary/50 transition-all cursor-pointer ${isRTL ? 'text-right' : ''}`}
      onClick={() => {
        setSelectedTrait(trait);
        fetchTraitDetail(trait.trait_id);
      }}
      data-testid={`trait-card-${trait.trait_id}`}
    >
      <div className={`flex items-start justify-between gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 mb-1">{trait.trait_text}</p>
          <div className={`flex items-center gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Badge className={`text-xs ${getStateColor(trait.state)}`}>
              {getStateLabel(trait.state)}
            </Badge>
            <span className="text-xs text-gray-500">
              {getDimensionLabel(trait.identity_dimension)}
            </span>
            {trait.has_contradictions && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {t('identityFormation.hasContradictions', 'Mixed evidence')}
              </Badge>
            )}
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 text-gray-400 flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
      </div>
      
      {/* Evidence strength indicator - NOT psychological certainty */}
      <div className="mt-3">
        <div className={`flex items-center justify-between text-xs text-gray-500 mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span>{t('identityFormation.evidenceStrength', 'Evidence strength')}</span>
          <span>{trait.evidence_count} {t('identityFormation.observations', 'observations')}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all ${
              trait.stability_score > 0.6 ? 'bg-green-500' : 
              trait.stability_score > 0.3 ? 'bg-amber-500' : 'bg-gray-300'
            }`}
            style={{ width: `${Math.min(trait.stability_score * 100, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-96" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" dir={isRTL ? 'rtl' : 'ltr'} data-testid="identity-formation-page">
      {/* Header */}
      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/personal/dashboard')}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
        </Button>
        <div className={isRTL ? 'text-right' : ''}>
          <h1 className="font-heading text-3xl font-bold text-gray-900">
            {t('identityFormation.title', 'Identity Formation')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('identityFormation.subtitle', 'Understand how HumaTwin sees you, and decide what becomes part of your identity')}
          </p>
        </div>
      </div>

      {/* Philosophy reminder - subtle */}
      <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
        <Shield className="w-4 h-4 flex-shrink-0" />
        <span>
          {t('identityFormation.philosophyReminder', 'HumaTwin observes patterns. You decide what becomes your identity. Nothing is automatic.')}
        </span>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-green-50 border-green-200" data-testid="confirmed-count">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-2xl font-bold text-green-700">{summary?.confirmed_traits_count || 0}</p>
              <p className="text-xs text-green-600">{t('identityFormation.confirmedTraits', 'Confirmed')}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-amber-50 border-amber-200" data-testid="candidate-count">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="p-2 bg-amber-100 rounded-lg">
              <Eye className="w-5 h-5 text-amber-600" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-2xl font-bold text-amber-700">
                {(summary?.emerging_patterns_count || 0) + (summary?.candidate_traits_count || 0)}
              </p>
              <p className="text-xs text-amber-600">{t('identityFormation.noticedPatterns', 'Noticed')}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-red-50 border-red-200" data-testid="contradictions-count">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-2xl font-bold text-red-700">{summary?.active_contradictions_count || 0}</p>
              <p className="text-xs text-red-600">{t('identityFormation.uncertainAreas', 'Uncertain')}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-purple-50 border-purple-200" data-testid="evidence-count">
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div className={isRTL ? 'text-right' : ''}>
              <p className="text-2xl font-bold text-purple-700">{summary?.total_evidence_count || 0}</p>
              <p className="text-xs text-purple-600">{t('identityFormation.totalEvidence', 'Evidence')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main content sections */}
      <div className="space-y-4">
        
        {/* Section 1: What HumaTwin is Noticing */}
        <Card className="border-2 border-amber-100" data-testid="noticed-section">
          <button
            onClick={() => toggleSection('noticed')}
            className={`w-full p-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Eye className="w-5 h-5 text-amber-600" />
              </div>
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="font-heading text-lg font-semibold text-gray-900">
                  {t('identityFormation.whatNoticingTitle', 'What HumaTwin is Noticing')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('identityFormation.whatNoticingDesc', 'Emerging patterns and candidate traits for your consideration')}
                </p>
              </div>
            </div>
            {expandedSection === 'noticed' ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {expandedSection === 'noticed' && (
            <div className="px-4 pb-4">
              {candidateTraits.length > 0 ? (
                <div className="space-y-3">
                  {candidateTraits.map(trait => renderTraitCard(trait))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">
                    {t('identityFormation.noPatterns', 'No patterns noticed yet. Continue your identity sessions and reflections.')}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Section 2: What HumaTwin Knows (Confirmed) */}
        <Card className="border-2 border-green-100" data-testid="confirmed-section">
          <button
            onClick={() => toggleSection('confirmed')}
            className={`w-full p-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-green-100 rounded-lg">
                <Fingerprint className="w-5 h-5 text-green-600" />
              </div>
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="font-heading text-lg font-semibold text-gray-900">
                  {t('identityFormation.whatKnowsTitle', 'What HumaTwin Knows')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('identityFormation.whatKnowsDesc', 'Stable identity elements you have confirmed')}
                </p>
              </div>
            </div>
            {expandedSection === 'confirmed' ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {expandedSection === 'confirmed' && (
            <div className="px-4 pb-4">
              {confirmedTraits.length > 0 ? (
                <div className="space-y-3">
                  {confirmedTraits.map(trait => renderTraitCard(trait, false))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Fingerprint className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">
                    {t('identityFormation.noConfirmed', 'No confirmed identity elements yet. Review the patterns HumaTwin noticed.')}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Section 3: What Remains Uncertain */}
        <Card className="border-2 border-red-100" data-testid="uncertain-section">
          <button
            onClick={() => toggleSection('uncertain')}
            className={`w-full p-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="font-heading text-lg font-semibold text-gray-900">
                  {t('identityFormation.uncertainTitle', 'What Remains Uncertain')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('identityFormation.uncertainDesc', 'Contradictory or weak evidence areas')}
                </p>
              </div>
            </div>
            {expandedSection === 'uncertain' ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {expandedSection === 'uncertain' && (
            <div className="px-4 pb-4">
              {contradictions.length > 0 ? (
                <div className="space-y-3">
                  {contradictions.map(contradiction => (
                    <div 
                      key={contradiction.contradiction_id}
                      className={`p-4 bg-white rounded-xl border border-red-200 ${isRTL ? 'text-right' : ''}`}
                    >
                      <div className={`flex items-start gap-3 mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-500 mb-2">
                            {t('identityFormation.contradictionNotice', 'The available evidence is mixed:')}
                          </p>
                          <div className="space-y-2">
                            <div className="p-2 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">&ldquo;{contradiction.claim_a}&rdquo;</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDate(contradiction.claim_a_first_seen)}
                              </p>
                            </div>
                            <div className="text-center text-xs text-gray-400">vs</div>
                            <div className="p-2 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-700">&ldquo;{contradiction.claim_b}&rdquo;</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatDate(contradiction.claim_b_first_seen)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 italic">
                        {t('identityFormation.contradictionNote', 'HumaTwin does not decide which is true. Both perspectives are preserved.')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Check className="w-12 h-12 mx-auto mb-3 text-green-300" />
                  <p className="text-sm">
                    {t('identityFormation.noContradictions', 'No contradictory evidence detected.')}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Section 4: What Changed Over Time */}
        <Card className="border-2 border-purple-100" data-testid="history-section">
          <button
            onClick={() => toggleSection('history')}
            className={`w-full p-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="p-2 bg-purple-100 rounded-lg">
                <History className="w-5 h-5 text-purple-600" />
              </div>
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="font-heading text-lg font-semibold text-gray-900">
                  {t('identityFormation.historyTitle', 'What Changed Over Time')}
                </h2>
                <p className="text-sm text-gray-500">
                  {t('identityFormation.historyDesc', 'Identity evolution and superseded traits')}
                </p>
              </div>
            </div>
            {expandedSection === 'history' ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className={`w-5 h-5 text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
            )}
          </button>
          
          {expandedSection === 'history' && (
            <div className="px-4 pb-4">
              {evolution.length > 0 ? (
                <div className="space-y-3">
                  {evolution.map(record => (
                    <div 
                      key={record.record_id}
                      className={`p-4 bg-white rounded-xl border border-purple-200 ${isRTL ? 'text-right' : ''}`}
                    >
                      <div className={`flex items-center gap-2 text-xs text-purple-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(record.transition_date)}</span>
                      </div>
                      
                      {record.previous_trait_text && (
                        <div className="mb-2">
                          <p className="text-xs text-gray-400 mb-1">{t('identityFormation.previouslyHeld', 'Previously:')}</p>
                          <p className="text-sm text-gray-500 line-through">&ldquo;{record.previous_trait_text}&rdquo;</p>
                        </div>
                      )}
                      
                      {record.new_trait_text && (
                        <div>
                          <p className="text-xs text-gray-400 mb-1">{t('identityFormation.nowHolds', 'Now:')}</p>
                          <p className="text-sm text-gray-900">&ldquo;{record.new_trait_text}&rdquo;</p>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-2 italic">{record.change_reason}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">
                    {t('identityFormation.noHistory', 'No identity changes recorded yet.')}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Trait Detail Dialog */}
      <Dialog open={showTraitDetail} onOpenChange={setShowTraitDetail}>
        <DialogContent className="max-w-lg">
          {loadingDetail ? (
            <div className="space-y-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : traitDetail && (
            <>
              <DialogHeader>
                <DialogTitle className={`text-xl ${isRTL ? 'text-right' : ''}`}>
                  {traitDetail.trait?.trait_text}
                </DialogTitle>
                <DialogDescription className={isRTL ? 'text-right' : ''}>
                  {t('identityFormation.traitDetailDesc', 'Review the evidence and decide how this pattern relates to your identity.')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                {/* Status badges */}
                <div className={`flex items-center gap-2 flex-wrap ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Badge className={getStateColor(traitDetail.trait?.state)}>
                    {getStateLabel(traitDetail.trait?.state)}
                  </Badge>
                  <Badge variant="outline">
                    {getDimensionLabel(traitDetail.trait?.identity_dimension)}
                  </Badge>
                </div>
                
                {/* HT-Core-02: Analysis Section */}
                {loadingAnalysis ? (
                  <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : traitAnalysis ? (
                  <div className="space-y-3">
                    {/* Evidence Strength - NOT psychological certainty */}
                    <div className="p-3 bg-gray-50 rounded-lg" data-testid="analysis-strength">
                      <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <TrendingUp className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                          {t('identityFormation.analysis.strengthTitle', 'Pattern Strength')}
                        </span>
                      </div>
                      <Badge className={getStrengthColor(traitAnalysis.strength_level)}>
                        {getStrengthLabel(traitAnalysis.strength_level)}
                      </Badge>
                      <p className="text-sm text-gray-600 mt-2">{traitAnalysis.strength_description}</p>
                    </div>
                    
                    {/* Temporal Profile */}
                    <div className="p-3 bg-blue-50 rounded-lg" data-testid="analysis-temporal">
                      <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Clock className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">
                          {t('identityFormation.analysis.temporalTitle', 'Temporal Profile')}
                        </span>
                      </div>
                      <Badge className={getEvolutionColor(traitAnalysis.evolution_state)}>
                        {getEvolutionLabel(traitAnalysis.evolution_state)}
                      </Badge>
                      <p className="text-sm text-blue-700 mt-2">{traitAnalysis.temporal_description}</p>
                      {traitAnalysis.is_dormant && (
                        <p className="text-xs text-blue-600 mt-1 italic">
                          {t('identityFormation.analysis.dormantNote', 'This pattern has been quiet recently but remains part of your history.')}
                        </p>
                      )}
                      {traitAnalysis.is_resurgent && (
                        <p className="text-xs text-blue-600 mt-1 italic">
                          {t('identityFormation.analysis.resurgentNote', 'This pattern has reappeared after a period of dormancy.')}
                        </p>
                      )}
                    </div>
                    
                    {/* Contextual Profile */}
                    <div className="p-3 bg-purple-50 rounded-lg" data-testid="analysis-context">
                      <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Layers className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-700">
                          {t('identityFormation.analysis.contextTitle', 'Context')}
                        </span>
                      </div>
                      <p className="text-sm text-purple-700">{traitAnalysis.context_description}</p>
                      {traitAnalysis.primary_contexts?.length > 0 && (
                        <div className={`flex flex-wrap gap-1 mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {traitAnalysis.primary_contexts.map((ctx, i) => (
                            <Badge key={i} variant="outline" className="text-xs text-purple-600 border-purple-300">
                              {getContextLabel(ctx)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Tensions / Evolution */}
                    {traitAnalysis.has_tensions && traitAnalysis.tension_description && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="analysis-tension">
                        <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-700">
                            {t('identityFormation.analysis.tensionTitle', 'Value Tension')}
                          </span>
                        </div>
                        <p className="text-sm text-amber-700">{traitAnalysis.tension_description}</p>
                        <p className="text-xs text-amber-600 mt-1 italic">
                          {t('identityFormation.analysis.tensionNote', 'Value tensions are normal. Both values can be important to you.')}
                        </p>
                      </div>
                    )}
                    
                    {traitAnalysis.possible_evolution && traitAnalysis.evolution_description && (
                      <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="analysis-evolution">
                        <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <GitBranch className="w-4 h-4 text-indigo-600" />
                          <span className="text-sm font-medium text-indigo-700">
                            {t('identityFormation.analysis.evolutionTitle', 'Possible Change')}
                          </span>
                        </div>
                        <p className="text-sm text-indigo-700">{traitAnalysis.evolution_description}</p>
                      </div>
                    )}
                    
                    {/* Key Factors (Explainability) */}
                    {traitAnalysis.key_factors?.length > 0 && (
                      <div className="p-3 bg-gray-50 rounded-lg" data-testid="analysis-factors">
                        <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Info className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">
                            {t('identityFormation.analysis.whyTitle', 'Why HumaTwin reached this assessment')}
                          </span>
                        </div>
                        <ul className={`space-y-1 ${isRTL ? 'text-right pr-4' : 'pl-4'}`}>
                          {traitAnalysis.key_factors.map((factor, i) => (
                            <li key={i} className="text-sm text-gray-600 list-disc">{factor}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Timeline Toggle */}
                    {traitTimeline?.events?.length > 0 && (
                      <div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setShowTimeline(!showTimeline)}
                          className={`w-full justify-between ${isRTL ? 'flex-row-reverse' : ''}`}
                          data-testid="toggle-timeline-btn"
                        >
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {t('identityFormation.analysis.viewTimeline', 'View pattern timeline')}
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${showTimeline ? 'rotate-180' : ''}`} />
                        </Button>
                        
                        {showTimeline && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-3" data-testid="timeline-view">
                            {traitTimeline.key_milestones?.length > 0 && (
                              <div className="text-xs text-gray-500 mb-2">
                                {traitTimeline.key_milestones.join(' → ')}
                              </div>
                            )}
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {traitTimeline.events.map((event, i) => (
                                <div key={i} className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-400 mt-2" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-400">{formatDate(event.event_date)}</p>
                                    <p className="text-sm text-gray-700">{event.description}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Fallback to basic summary if analysis not available */
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className={`flex items-center justify-between text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="text-gray-600">{t('identityFormation.evidenceLabel', 'Evidence')}</span>
                      <span className="font-medium">{traitDetail.summary?.total_evidence} {t('identityFormation.observations', 'observations')}</span>
                    </div>
                    <div className={`flex items-center justify-between text-sm mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="text-gray-600">{t('identityFormation.timeSpan', 'Time span')}</span>
                      <span className="font-medium">{traitDetail.summary?.time_span_days} {t('identityFormation.days', 'days')}</span>
                    </div>
                    <div className={`flex items-center justify-between text-sm mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <span className="text-gray-600">{t('identityFormation.sourceTypes', 'Source types')}</span>
                      <span className="font-medium">{traitDetail.summary?.source_diversity}</span>
                    </div>
                  </div>
                )}
                
                {/* Supporting evidence preview */}
                {traitDetail.supporting_evidence?.length > 0 && (
                  <div>
                    <h4 className={`text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                      {t('identityFormation.supportingEvidence', 'Supporting evidence')}
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {traitDetail.supporting_evidence.slice(0, 3).map((ev, i) => (
                        <div key={i} className="p-2 bg-gray-50 rounded text-sm text-gray-600">
                          <p className="text-xs text-gray-400">
                            {ev.provenance?.source_description}
                          </p>
                          <p className="mt-1">{ev.evidence?.content_summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Contradicting evidence */}
                {traitDetail.contradicting_evidence?.length > 0 && (
                  <div>
                    <h4 className={`text-sm font-medium text-amber-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {t('identityFormation.contradictingEvidence', 'Contradicting evidence')}
                    </h4>
                    <div className="space-y-2">
                      {traitDetail.contradicting_evidence.map((ev, i) => (
                        <div key={i} className="p-2 bg-amber-50 rounded text-sm text-amber-700">
                          <p>{ev.evidence?.content_summary}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Actions - only show for non-confirmed traits */}
              {traitDetail.trait?.state !== 'confirmed' && traitDetail.trait?.state !== 'rejected' && (
                <DialogFooter className={`flex-col sm:flex-row gap-2 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTrait(traitDetail.trait);
                            setShowRejectDialog(true);
                          }}
                          disabled={actionLoading}
                          data-testid="reject-trait-btn"
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t('identityFormation.reject', 'Reject')}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t('identityFormation.rejectTooltip', "This doesn't represent me")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedTrait(traitDetail.trait);
                      setReframeText(traitDetail.trait?.trait_text || '');
                      setShowReframeDialog(true);
                    }}
                    disabled={actionLoading}
                    data-testid="reframe-trait-btn"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {t('identityFormation.reframe', 'Reframe')}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedTrait(traitDetail.trait);
                      setShowContextualizeDialog(true);
                    }}
                    disabled={actionLoading}
                    data-testid="contextualize-trait-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    {t('identityFormation.contextualize', 'Contextualize')}
                  </Button>
                  
                  <Button
                    size="sm"
                    onClick={() => handleConfirm(traitDetail.trait?.trait_id)}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="confirm-trait-btn"
                  >
                    {actionLoading ? (
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-1" />
                    )}
                    {t('identityFormation.confirm', 'Confirm')}
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={isRTL ? 'text-right' : ''}>
              {t('identityFormation.rejectTitle', 'Reject this pattern?')}
            </DialogTitle>
            <DialogDescription className={isRTL ? 'text-right' : ''}>
              {t('identityFormation.rejectDesc', "HumaTwin will not ask about this pattern again unless meaningful new evidence appears.")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <label className={`text-sm font-medium text-gray-700 block mb-2 ${isRTL ? 'text-right' : ''}`}>
                {t('identityFormation.rejectReasonLabel', 'Why does this not represent you? (optional)')}
              </label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={t('identityFormation.rejectReasonPlaceholder', 'Help HumaTwin understand...')}
                className={isRTL ? 'text-right' : ''}
              />
            </div>
            
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <input
                type="checkbox"
                id="permanent-reject"
                checked={rejectPermanent}
                onChange={(e) => setRejectPermanent(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="permanent-reject" className="text-sm text-gray-600">
                {t('identityFormation.permanentReject', 'Never ask about this pattern again')}
              </label>
            </div>
          </div>
          
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={actionLoading}
            >
              {actionLoading ? t('common.loading', 'Loading...') : t('identityFormation.confirmReject', 'Reject Pattern')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reframe Dialog */}
      <Dialog open={showReframeDialog} onOpenChange={setShowReframeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={isRTL ? 'text-right' : ''}>
              {t('identityFormation.reframeTitle', 'Reframe in your own words')}
            </DialogTitle>
            <DialogDescription className={isRTL ? 'text-right' : ''}>
              {t('identityFormation.reframeDesc', "If HumaTwin's wording doesn't feel right, express it how you would.")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={reframeText}
              onChange={(e) => setReframeText(e.target.value)}
              placeholder={t('identityFormation.reframePlaceholder', 'Express this in your own words...')}
              className={isRTL ? 'text-right' : ''}
              rows={4}
            />
          </div>
          
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowReframeDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleReframe}
              disabled={actionLoading || !reframeText.trim()}
            >
              {actionLoading ? t('common.loading', 'Loading...') : t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contextualize Dialog */}
      <Dialog open={showContextualizeDialog} onOpenChange={setShowContextualizeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={isRTL ? 'text-right' : ''}>
              {t('identityFormation.contextualizeTitle', 'Add context')}
            </DialogTitle>
            <DialogDescription className={isRTL ? 'text-right' : ''}>
              {t('identityFormation.contextualizeDesc', 'For patterns that are true in some situations but not universally.')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={contextualNote}
              onChange={(e) => setContextualNote(e.target.value)}
              placeholder={t('identityFormation.contextualizePlaceholder', 'In what context does this apply? e.g., "Only at work" or "When under stress"')}
              className={isRTL ? 'text-right' : ''}
              rows={4}
            />
          </div>
          
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowContextualizeDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleContextualize}
              disabled={actionLoading || !contextualNote.trim()}
            >
              {actionLoading ? t('common.loading', 'Loading...') : t('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
